import { ArrowLeft } from 'lucide-react';
import { useContext } from 'react';
import { NavLink, Outlet, useParams } from 'react-router';
import { paths } from '../../app/paths';
import { Brand } from '../../components/brand';
import { UserMenu } from '../../components/user-menu';
import { useI18n } from '../../i18n/i18n';
import type { MessageKey } from '../../i18n/en';
import { cn } from '../../lib/cn';
import { ArchivedNotice } from '../../workspaces/archived-notice';
import { CurrentWorkspaceContext, useCurrentWorkspace } from '../../workspaces/current-workspace';
import { can, useLastWorkspace, type WorkspacePermission } from '../../workspaces/workspaces-api';

interface Item {
  to: string;
  label: MessageKey;
  end?: boolean;
}

/**
 * Настройки — отдельные страницы с левым меню: аккаунт (профиль и безопасность, все
 * пространства) и открытое пространство (общее, участники, приглашения, журнал — по правам).
 */
export function SettingsLayout() {
  const { t } = useI18n();
  const workspace = useContext(CurrentWorkspaceContext);
  const { workspaceId } = useParams();
  // Вне пространства (профиль, список) «назад» ведёт в последнее открытое.
  const last = useLastWorkspace();
  const backTo = workspace?.id ?? workspaceId ?? last.data?.workspace?.id;

  const account: Item[] = [
    { to: paths.profile, label: 'profile.title' },
    { to: paths.security, label: 'security.title' },
    { to: paths.workspaces, label: 'menu.allWorkspaces' },
  ];

  return (
    <div className="flex min-h-dvh bg-background">
      <aside className="sticky top-0 m-4 flex h-[calc(100dvh-2rem)] w-[260px] shrink-0 flex-col gap-6 overflow-y-auto rounded-[12px] border border-border bg-card px-4 py-[22px] shadow-[0_1px_2px_rgba(17,24,39,0.04),0_4px_12px_rgba(17,24,39,0.04)]">
        <Brand to={backTo === undefined ? paths.root : paths.workspace(backTo)} wordmark />
        {backTo !== undefined && (
          <NavLink
            to={paths.workspace(backTo)}
            className="-mt-2 inline-flex items-center gap-1.5 px-2 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-4" />
            {t('settings.back')}
          </NavLink>
        )}
        <NavGroup title={t('settings.account')} items={account} />
        {workspace !== null && <WorkspaceNav />}
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex justify-end px-6 py-4">
          <UserMenu {...(backTo !== undefined ? { workspaceId: backTo } : {})} />
        </header>
        <main className="mx-auto w-full max-w-3xl flex-1 space-y-6 px-6 pb-16">
          {workspace !== null && <ArchivedNotice />}
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function WorkspaceNav() {
  const { t } = useI18n();
  const workspace = useCurrentWorkspace();
  const sections: Array<Item & { permission: WorkspacePermission }> = [
    {
      to: paths.workspaceSettings(workspace.id),
      label: 'workspace.tab.overview',
      permission: 'workspace.read',
      end: true,
    },
    {
      to: paths.workspaceSettings(workspace.id, 'members'),
      label: 'workspace.tab.members',
      permission: 'members.read',
    },
    {
      to: paths.workspaceSettings(workspace.id, 'invitations'),
      label: 'workspace.tab.invitations',
      permission: 'invitations.read',
    },
    {
      to: paths.workspaceSettings(workspace.id, 'audit'),
      label: 'workspace.tab.audit',
      permission: 'audit.read',
    },
  ];
  return (
    <NavGroup
      title={`${t('settings.workspace')} · ${workspace.name}`}
      items={sections.filter((section) => can(workspace, section.permission))}
    />
  );
}

function NavGroup({ title, items }: { title: string; items: Item[] }) {
  const { t } = useI18n();
  return (
    <nav className="flex flex-col gap-1">
      <p className="truncate px-2 pb-2.5 text-2xs font-semibold tracking-[0.05em] text-hint uppercase">
        {title}
      </p>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end ?? false}
          className={({ isActive }) =>
            cn(
              'block truncate rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-background',
              isActive && 'bg-primary-super font-medium text-primary hover:bg-primary-super',
            )
          }
        >
          {t(item.label)}
        </NavLink>
      ))}
    </nav>
  );
}
